import {DefaultArcObject, Selection} from 'd3';
import * as d3 from 'd3';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {LEGACY_PART_STROKE_COLOR} from 'molbio/maps/BiointelligentLinksConstants';
import {PartsLayout} from 'molbio/maps/circular/CircularLayout';
import LabeledSegmentsRingView from 'molbio/maps/circular/LabeledSegmentsRingView';
import {
  splitPartsModelsIntoHighlightRegions,
  makeViewParts,
  ViewPart,
} from 'molbio/maps/translateLinksToHighlightRegions';

/*
 * Note that there is some dangerous typing going on here. All other circular views extending from
 * SegmentsRingView use a data type that extends RegionSelectable appropriately and are therefore safe
 * because they conform to the interface requirements of the MapsUtil helper functions invoked by
 * SegmentsRingView (namely getMax and getLayers).
 *
 * PartsView breaks this pattern by using ViewPart as the data model so it doesn't meet the interface
 * requirements of some SegmentsRingView method. In practice, this does not raise errors due to the
 * redefinition of updateLayers and likely some other logic.
 */
// @ts-expect-error
class PartsView extends LabeledSegmentsRingView<PartsLayout, ViewPart> {
  static initClass(): void {
    this.prototype.className = 'parts-label';
    this.prototype.singularClassName = 'part-label';
    this.prototype.tagName = 'g';

    // Need to set contextMenuData to null to default to showing the parts context menu.
    // Should revisit this behavior to match other features.
    // @ts-expect-error
    this.prototype.contextMenuData = null;
  }

  shouldCaptureLeftClick(): boolean {
    return false;
  }

  shouldCaptureDoubleClick(): boolean {
    return true;
  }

  contextMenuOptions(d: ViewPart): MenuChoicesOptions {
    return {tabstate: this.tabstate};
  }

  arc(d: ViewPart): d3.Arc<unknown, DefaultArcObject> {
    return d3.arc();
  }

  data(): Array<ViewPart> {
    const start = this.model.get('leftBP');
    const end = this.model.get('rightBP');
    const sequence = this.model.get('sequence');
    const parts = sequence.parts().getSequencePartsBetween(start, end);
    return makeViewParts(splitPartsModelsIntoHighlightRegions(parts, sequence));
  }

  stroke(d: ViewPart): string {
    return LEGACY_PART_STROKE_COLOR;
  }

  fill(d: ViewPart): string {
    return d.color();
  }

  baseLine(layer: number): number {
    return this.radius(layer + 1); // + 1 renders on top.
  }

  opacity(d: ViewPart): number {
    return 0.4;
  }

  registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:bases', () => {
      this.invalidateData();
    });
  }

  updateRadius(): void {
    this.radius.range([this.getInnerRadius() - 5, this.layout.get('DNA')!.get('outerRadius') + 5]);
  }

  updateLayers(data: Array<ViewPart>): void {
    // eslint-disable-next-line no-restricted-syntax
    _.each(data, (datum) => datum.set({annularLayer: 0}));
    this.layout.set('outerRadius', this.getInnerRadius());
  }

  handleText(selector: Selection<SVGGElement, ViewPart, SVGGElement, unknown>): void {
    if (!BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      super.handleText(selector);
    }
  }

  // @ts-expect-error We helpfully do something custom with the part overlay here instead of making it a
  // Selectable
  clickAction(e: Event, d: ViewPart): void {}

  // @ts-expect-error We helpfully do something custom with the part overlay here instead of making it a
  // Selectable
  dblClickAction(e: Event, d: ViewPart): void {
    // The sequence double click handler already contains the logic for selecting the correct part regions.
    // Default to selecting the whole sequence when the region is an overlap, which is the same behavior
    // as double clicking on a location with no parts.
    this.model.get('sequence').handleDoubleClick(0, this.model.get('sequence').length());
  }
}
PartsView.initClass();

export default PartsView;
